import React, { useCallback, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faSync, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import { schwabApi } from '../api/schwabApi';
import { useAuth } from '../contexts/AuthContext';

const TXN_TYPES = [
  'TRADE',
  'RECEIVE_AND_DELIVER',
  'DIVIDEND_OR_INTEREST',
  'ACH_RECEIPT',
  'ACH_DISBURSEMENT',
  'CASH_RECEIPT',
  'CASH_DISBURSEMENT',
  'ELECTRONIC_FUND',
  'WIRE_OUT',
  'WIRE_IN',
  'JOURNAL',
  'MEMORANDUM',
  'MARGIN_CALL',
  'MONEY_MARKET',
  'SMA_ADJUSTMENT',
];

function formatCurrency(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function formatTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function maskAccount(num) {
  if (!num) return '—';
  const s = String(num);
  return s.length <= 4 ? s : `••••${s.slice(-4)}`;
}

export default function SchwabTransactionsPanel({
  viewMode,
  selectedAccount,
}) {
  const { maskAmounts } = useAuth();
  const money = (v) => (maskAmounts ? '••••' : formatCurrency(v));

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [types, setTypes] = useState('TRADE');
  const [days, setDays] = useState('30');
  const [symbol, setSymbol] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setWarnings([]);
      const params = {
        types,
        days,
        symbol: symbol.trim() || undefined,
        accountNumber:
          viewMode === 'single' && selectedAccount ? selectedAccount : undefined,
      };
      const res = await schwabApi.getTransactions(params);
      setTransactions(res.data || []);
      if (res.errors?.length) setWarnings(res.errors);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [types, days, symbol, viewMode, selectedAccount]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink flex-1 sm:flex-none min-w-[7rem]"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="180">Last 180 days</option>
            <option value="365">Last year</option>
          </select>
          <select
            value={types}
            onChange={(e) => setTypes(e.target.value)}
            className="text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink flex-1 sm:flex-none min-w-[7rem]"
          >
            {TXN_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <input
            type="search"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            placeholder="Symbol filter"
            className="flex-1 min-w-[6rem] sm:w-28 text-xs px-2 py-1.5 rounded-md border border-hairline bg-canvas text-ink"
          />
          <span className="text-xs text-muted num">{transactions.length} txns</span>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-hairline text-muted hover:text-ink"
        >
          <FontAwesomeIcon icon={faSync} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md border border-hairline text-sm text-bear bg-elevated">
          <FontAwesomeIcon icon={faExclamationCircle} className="mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {warnings.length > 0 && (
        <div className="px-3 py-2 rounded-md border border-hairline text-xs text-muted bg-elevated">
          Some accounts failed to load: {warnings.join(' · ')}
        </div>
      )}

      <div className="lt-card overflow-hidden">
        {loading && transactions.length === 0 ? (
          <div className="py-10 text-center">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-muted">
            No transactions for this filter.
          </div>
        ) : (
          <>
            <div className="md:hidden divide-y divide-hairline">
              {transactions.map((t) => {
                const net = Number(t.netAmount);
                return (
                  <div
                    key={`${t.accountNumber}-${t.activityId}`}
                    className="px-3 py-3 space-y-2"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-semibold text-ink">{t.symbol || t.type || '—'}</div>
                        <div className="text-[10px] text-muted mt-0.5 truncate">
                          {t.type}
                          {t.assetType ? ` · ${t.assetType}` : ''}
                          {viewMode === 'all' ? ` · ${maskAccount(t.accountNumber)}` : ''}
                        </div>
                      </div>
                      <div
                        className={`num text-sm shrink-0 ${
                          Number.isFinite(net) ? (net >= 0 ? 'text-bull' : 'text-bear') : 'text-ink'
                        }`}
                      >
                        {t.netAmount != null ? money(t.netAmount) : '—'}
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-[11px]">
                      <div>
                        <div className="text-muted uppercase tracking-wide text-[9px]">Qty</div>
                        <div className="num text-ink">{t.quantity ?? '—'}</div>
                      </div>
                      <div>
                        <div className="text-muted uppercase tracking-wide text-[9px]">Price</div>
                        <div className="num text-ink">
                          {t.price != null ? money(t.price) : '—'}
                        </div>
                      </div>
                      <div>
                        <div className="text-muted uppercase tracking-wide text-[9px]">Time</div>
                        <div className="num text-ink text-[10px]">
                          {formatTime(t.time || t.tradeDate)}
                        </div>
                      </div>
                    </div>
                    {t.description && (
                      <div className="text-[11px] text-muted line-clamp-2">{t.description}</div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[960px]">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-[0.12em] text-muted border-b border-hairline">
                    <th className="px-3 py-2">Time</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Symbol</th>
                    <th className="px-3 py-2">Qty</th>
                    <th className="px-3 py-2">Price</th>
                    <th className="px-3 py-2">Net</th>
                    <th className="px-3 py-2">Description</th>
                    {viewMode === 'all' && <th className="px-3 py-2">Account</th>}
                    <th className="px-3 py-2">Id</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t) => {
                    const net = Number(t.netAmount);
                    return (
                      <tr
                        key={`${t.accountNumber}-${t.activityId}`}
                        className="border-b border-hairline last:border-0 hover:bg-elevated/50"
                      >
                        <td className="px-3 py-2 text-xs num text-muted whitespace-nowrap">
                          {formatTime(t.time || t.tradeDate)}
                        </td>
                        <td className="px-3 py-2 text-xs">{t.type}</td>
                        <td className="px-3 py-2 font-medium text-ink">
                          {t.symbol || '—'}
                          {t.assetType && (
                            <div className="text-[10px] text-muted">{t.assetType}</div>
                          )}
                        </td>
                        <td className="px-3 py-2 num">{t.quantity ?? '—'}</td>
                        <td className="px-3 py-2 num">{t.price != null ? money(t.price) : '—'}</td>
                        <td
                          className={`px-3 py-2 num ${
                            Number.isFinite(net) ? (net >= 0 ? 'text-bull' : 'text-bear') : ''
                          }`}
                        >
                          {t.netAmount != null ? money(t.netAmount) : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-muted max-w-[240px] truncate" title={t.description || ''}>
                          {t.description || '—'}
                        </td>
                        {viewMode === 'all' && (
                          <td className="px-3 py-2 text-xs num text-muted">
                            {maskAccount(t.accountNumber)}
                          </td>
                        )}
                        <td className="px-3 py-2 text-xs num text-muted">{t.activityId}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
